import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { PVC_TEST_ID, TestProvider } from '../../providers/test-provider';
import { PvcMeasure } from '../../../common/models/pvc-measure';
import { Observable, Subject, Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../../common/components/confirm-dialog/confirm-dialog';
import { NewMeasureFormComponent } from '../../components/new-measure-form/new-measure-form';
import { TestItemRightsList, TestRights } from '../../providers/test-rights';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { CurrentUser } from "../../../common/providers/current-user";
import { CurrentUserRolesProvider } from '../../../organizations/providers/current-user-roles-provider';
import { ConfigProvider } from "../../../config-provider.service";
import { Title } from '@angular/platform-browser';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { shareReplay, tap } from 'rxjs/operators';
import { LangsProvider } from "../../../common/providers/langs-provider";
import { Organization } from '../../../common/models/organization';
import { Test } from "../../../common/models/test";
import { User } from "../../../common/models/user";
import { Error404Component } from "../../../common/components/error-404/error-404";
import { AsyncPipe, DatePipe, NgClass, NgFor, NgIf } from "@angular/common";
import { Error401Component } from "../../../common/components/error-401/error-401";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatCardModule } from "@angular/material/card";
import { MatChipsModule } from "@angular/material/chips";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { MatMiniFabButton } from "@angular/material/button";
import { TestGaugeChartComponent } from "../../components/gauge-chart/test-gauge-chart";
import { TestEmptyGaugeChartComponent } from "../../components/empty-gauge-chart/test-empty-gauge-chart";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { TestMultiLineBorgChartComponent } from "../../components/multi-line-borg-chart/test-multi-line-borg-chart";
import { TestMultiLineChartComponent } from "../../components/multi-line-chart/test-multi-line-chart";
import { GroupedMeasureValuesPipe } from "../../pipes/grouped-measure-values-pipe";
import { MainActionComponent } from "../../../common/components/main-action/main-action";
import { FullWidthLoaderComponent } from "../../../common/components/full-width-loader/full-width-loader";
import { CenterLoaderComponent } from "../../../common/components/center-loader/center-loader";
import { MatDivider } from "@angular/material/divider";

@Component({
  selector: 'app-test-generic-container',
  standalone: true,
  templateUrl: './generic-container.component.html',
  styleUrls: ['./generic-container.scss'],
  imports: [
    Error404Component,
    NgIf,
    Error401Component,
    NgClass,
    FlexLayoutModule,
    TranslateModule,
    MatCardModule,
    MatChipsModule,
    AppDatePipe,
    MatMiniFabButton,
    AsyncPipe,
    NgFor,
    TestGaugeChartComponent,
    TestEmptyGaugeChartComponent,
    MatMenuModule,
    MatIconModule,
    TestMultiLineBorgChartComponent,
    TestMultiLineChartComponent,
    GroupedMeasureValuesPipe,
    MainActionComponent,
    FullWidthLoaderComponent,
    CenterLoaderComponent,
    MatDivider,
    DatePipe,
  ]
})
export class TestGenericContainerComponent implements OnInit, OnChanges, OnDestroy {

    public Ready: Promise<boolean> = new Promise((resolve, reject) => resolve(false));
    public pageTitle = 'GenericTest';
    public isLoading = true;
    public is401 = false;
    public is404 = false;
    public rights: TestRights = new TestRights(this.utils);
    public itemRights: TestItemRightsList | null = null;

    public graphClass = 'generic-chart';

    @Input()
    public isMobile = false;

    @Input()
    public organization: Organization = {} as Organization;

    @Input()
    public athleteUser: User = {} as User;

    @Input()
    public test: Test = {} as Test;
    public tests: Test[] = [];

    public addDialog: MatDialogRef<NewMeasureFormComponent> = {} as MatDialogRef<any>;
    public editDialog: MatDialogRef<NewMeasureFormComponent> = {} as MatDialogRef<any>;
    public deleteDialog: MatDialogRef<ConfirmDialogComponent> = {} as MatDialogRef<any>;

    public data$: Observable<any> = new Observable<any>();
    public metrics$: Observable<any> = new Observable<any>();
    private metricsUngrouped$: Observable<any> = new Observable<any>();

    @Output()
    public updateSideMenuEvent = new EventEmitter();

    public subject: Subject<any> = new Subject();

    public currentMeasure = 'borg';

    public date: string = '';

    public hasData = false;

    public currentSerieCategory: number|null = null;

    public isPvcTest = false;
    public pvcData: PvcMeasure[] = [];
    public selectedPvcMeasure: any = null;
    public currentPvcChart: 'pvc' | 'weight' | 'size' = 'pvc';
    public pvcOffsetSeries: any[] = [];
    public pvcActiveSeries: any[] = [];

    private measureId: number |null= null;
    private grouped = true;
    private subscriptions: Subscription[] = [];

    constructor(
        public conf: ConfigProvider,
        private title: Title,
        private utils: UtilsProvider,
        private currentUser: CurrentUser,
        private currentUserRoles: CurrentUserRolesProvider,
        private dialog: MatDialog,
        private langsProvider: LangsProvider,
        private testProvider: TestProvider,
        private toaster: MatSnackBar,
        private translator: TranslateService,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
      if (changes['test'] && !changes['test'].isFirstChange()) {
        this.componentLoad()
      }
    }

    public ngOnInit() {
        this.componentLoad()
        this.subscriptions.push(this.metricsUngrouped$.subscribe()); // pre-load for form
    }

    private componentLoad() {
      this.isLoading = true;
      this.createPageTitle();

      this.isPvcTest = this.test.id === PVC_TEST_ID;
      this.grouped = !([6, 7].includes(this.test.id!));

      this.loadData();

      // TODO: store test 'grouped' attribute in DB instead of hardcoding here
      this.metrics$ = this.testProvider.getMetrics(
        this.organization,
        this.athleteUser,
        this.test.id!,
        {
          lang: this.langsProvider.getBestLangId(),
          grouped: this.grouped,
        }
      ).pipe(shareReplay());

      this.metricsUngrouped$ = this.testProvider.getMetrics(
        this.organization,
        this.athleteUser,
        this.test.id!,
        {
          lang: this.langsProvider.getBestLangId(),
          grouped: false,
        }
      ).pipe(shareReplay());
      this.isLoading = false;
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public add() {
        this.addDialog = this.dialog.open(NewMeasureFormComponent, {
            data: {
                dialogTitle: 'TestNewMeasure',
                organization: this.organization,
                athlete: this.athleteUser,
                test: this.test,
                metrics$: this.metricsUngrouped$,
            }
        });

        this.subscriptions.push(
            this.addDialog.afterClosed().subscribe((data:any) => {
                if (data) {
                    this.updateSideMenuEvent.emit();
                    this.loadData();
                    this.toaster.open(this.translator.instant('TestMeasureSaved'), '', { duration: 5000 });
                }
            })
        );
    }

    public edit(measureId: number) {
        this.editDialog = this.dialog.open(NewMeasureFormComponent, {
            data: {
                dialogTitle: 'TestEditMeasure',
                organization: this.organization,
                athlete: this.athleteUser,
                test: this.test,
                measureId,
                data$: this.data$,
                metrics$: this.metricsUngrouped$,
            }
        });

        this.subscriptions.push(
            this.editDialog.afterClosed().subscribe((data:any) => {
                if (data) {
                    this.updateSideMenuEvent.emit();

                    this.loadData({measureId});

                    this.toaster.open(this.translator.instant('TestMeasureEdited'), '', { duration: 5000 });
                }
            })
        );
    }

    public delete(measureId: number) {
        this.deleteDialog = this.dialog.open(ConfirmDialogComponent, {
            data: {
                confirmText: this.translator.instant('TestMeasureConfirmDelete'),
                confirmActionText: this.translator.instant('Delete'),
                color: 'accent'
            }
        });

        this.subscriptions.push(
            this.deleteDialog.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.testProvider.deleteMeasure(
                        this.organization,
                        this.athleteUser,
                        this.test,
                        measureId
                    ).subscribe({
                        next: () => {
                          this.notifyDeleted();
                          this.updateSideMenuEvent.emit();
                          this.loadData();
                          this.toaster.open(this.translator.instant('TestMeasureDeleted'), '', { duration: 5000 });
                        },
                        error: (error) => {
                            this.toaster.open(this.translator.instant('TestMeasureDeleteError'), '', { duration: 5000 });
                        }
                    });
                }
            })
        );
    }

    public loadData(queryParams:any = {}) {
        this.hasData = false;
        // TODO: check rights on the API and check if 403 is returned
        this.rights.evaluateScreenRights({
            rolesIds: this.currentUserRoles.getRolesIds(this.organization.id!)
        });
        if (!this.rights.LIST) {
            this.is401 = true;
        }

        queryParams['lang'] = this.langsProvider.getBestLangId();
        queryParams['grouped'] = this.grouped;
        this.data$ = this.testProvider.getMeasures(
            this.organization,
            this.athleteUser.athlete!,
            this.test,
            queryParams
        ).pipe(tap(measure => {
            if (this.isPvcTest) {
                this.pvcData = Array.isArray(measure) ? measure : [];
                this.hasData = this.pvcData.length > 0;
                if (this.hasData) {
                    this.buildPvcOffsetSeries();
                    const last = this.pvcData[this.pvcData.length - 1];
                    this.selectPvcMeasure(last);
                }
            } else if (measure && measure.selected?.length > 0) {
                this.date = measure.selected[0].date;
                this.measureId = measure.selected[0].measureId;
                this.currentSerieCategory = measure.selected[0].category;
                this.hasData = null !== measure.selected[0].measureId;
            }
            this.isLoading = false;
        }));
    }

    public onPointClickedEvent(point:any) {
        this.measureId = point.id;

        this.loadData({measureId: this.measureId, ts: point.x});
    }

    public onPvcPointClicked(point: any) {
        const measure = this.pvcData.find(m => new Date(m.date).getTime() === point.x);
        if (measure) {
            this.selectPvcMeasure(measure);
        }
    }

    public onPvcChartSelectorClicked(type: 'pvc' | 'weight' | 'size') {
        this.currentPvcChart = type;

        if (type === 'pvc') {
            this.buildPvcOffsetSeries();
        } else {
            const label = type === 'weight' ? 'Weight' : 'Size';
            const key = type as keyof PvcMeasure;
            const data = this.pvcData
                .map(item => [new Date(item.date).getTime(), item[key] as number])
                .sort((a, b) => a[0] - b[0]);

            this.pvcActiveSeries = [{ name: this.translator.instant(label), data }];
        }

        setTimeout(() => {
            this.subject.next('reflow');
        }, 10);
    }

    private selectPvcMeasure(measure: PvcMeasure) {
        this.selectedPvcMeasure = {
            ...measure,
            x: new Date(measure.date).getTime(),
        };
    }

    private buildPvcOffsetSeries() {
        const data = this.pvcData
            .map(item => [new Date(item.date).getTime(), item.axis_y])
            .sort((a, b) => a[0] - b[0]);

        this.pvcOffsetSeries = [{ name: this.translator.instant('PvcTooltipOffset'), data }];
    }

    public trackByMeasureId(_index: number, item: any): number {
        return item?.id;
    }

    public notifyDeleted() {
        this.subject.next('delete');
    }

    public onChartSelectorClicked(type: string) {
        this.currentMeasure = type;

        setTimeout(() => {
            this.subject.next('reflow');
        }, 10);
    }

    private createPageTitle() {
        this.pageTitle = this.test.name ?? '';
        this.title.setTitle(this.pageTitle + ' | ' + this.conf.platformName);
    }
}
